import { API } from '@/core/Api';

// 이미지를 이미지로 변환하는 AI 서비스
export class ImageToImageAI {
  /**
   * Flux Pro Redux - 고품질 이미지-투-이미지 변환
   * @param imageFile 원본 이미지 파일
   * @param prompt 변환 설명
   * @param options 추가 옵션들
   * @returns 변환된 이미지 정보
   */
  async generateWithFluxProRedux(
    imageFile: File,
    prompt: string,
    options: {
      aspect_ratio?: string; // 화면 비율 (예: "1:1", "16:9")
      num_images?: number; // 생성할 이미지 수 (1-4)
      seed?: number; // 시드값 (재현성을 위해)
      sync_mode?: boolean; // 동기 모드
      enable_safety_checker?: boolean; // 안전성 검사
      output_format?: 'jpeg' | 'png'; // 출력 형식
      safety_tolerance?: string; // 안전성 허용도 ("1"-"6")
      raw?: boolean; // Raw 모드 (자연스러운 스타일)
      image_prompt_strength?: number; // 이미지 프롬프트 강도 (0-1)
      signal?: AbortSignal;
    } = {},
  ) {
    // 이미지를 Base64로 변환
    const imageBase64 = await this.convertFileToBase64(imageFile);

    const requestData = {
      prompt,
      image_base64: imageBase64,
      aspect_ratio: options.aspect_ratio || '1:1',
      num_images: options.num_images || 1,
      seed: options.seed,
      sync_mode: options.sync_mode || false,
      enable_safety_checker: options.enable_safety_checker || true,
      output_format: options.output_format || 'jpeg',
      safety_tolerance: options.safety_tolerance || '2',
      raw: options.raw || false,
      image_prompt_strength: options.image_prompt_strength || 0.8,
    };

    const response = await API.post('/api/ai/image-to-image/flux-pro-redux', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Flux Pro Redux 변환에 실패했습니다.');
    }
  }

  /**
   * Flux Kontext - 고급 이미지 편집
   * @param imageFile 원본 이미지 파일
   * @param prompt 변환 설명
   * @param options 추가 옵션들
   * @returns 변환된 이미지 정보
   */
  async generateWithFluxKontext(
    imageFile: File,
    prompt: string,
    options: {
      aspect_ratio?: string; // 화면 비율
      num_images?: number; // 생성할 이미지 수
      seed?: number; // 시드값
      guidance_scale?: number; // CFG 가이던스 스케일 (1-20)
      sync_mode?: boolean; // 동기 모드
      output_format?: 'jpeg' | 'png'; // 출력 형식
      safety_tolerance?: string; // 안전성 허용도
      signal?: AbortSignal;
    } = {},
  ) {
    // 이미지를 Base64로 변환
    const imageBase64 = await this.convertFileToBase64(imageFile);

    const requestData = {
      prompt,
      image_base64: imageBase64,
      aspect_ratio: options.aspect_ratio || '1:1',
      num_images: options.num_images || 1,
      seed: options.seed,
      guidance_scale: options.guidance_scale || 7.5,
      sync_mode: options.sync_mode || false,
      output_format: options.output_format || 'png',
      safety_tolerance: options.safety_tolerance || '2',
    };

    const response = await API.post('/api/ai/image-to-image/flux-kontext', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Flux Kontext 변환에 실패했습니다.');
    }
  }

  /**
   * Flux Kontext Multi - 여러 이미지 동시 처리
   * @param imageFiles 원본 이미지 파일들 (최대 4개)
   * @param prompt 변환 설명
   * @param options 추가 옵션들
   * @returns 변환된 이미지들 정보
   */
  async generateWithFluxKontextMulti(
    imageFiles: File[],
    prompt: string,
    options: {
      aspect_ratio?: string; // 화면 비율
      num_images?: number; // 생성할 이미지 수
      seed?: number; // 시드값
      guidance_scale?: number; // CFG 가이던스 스케일
      sync_mode?: boolean; // 동기 모드
      output_format?: 'jpeg' | 'png'; // 출력 형식
      safety_tolerance?: string; // 안전성 허용도
      signal?: AbortSignal;
    } = {},
  ) {
    if (imageFiles.length === 0) {
      throw new Error('최소 1개의 이미지가 필요합니다.');
    }

    if (imageFiles.length > 4) {
      throw new Error('최대 4개의 이미지까지 처리 가능합니다.');
    }

    // 모든 이미지를 Base64로 변환
    const imageBase64Array = await Promise.all(
      imageFiles.map((file) => this.convertFileToBase64(file)),
    );

    const requestData = {
      prompt,
      image_urls: imageBase64Array, // Base64 배열을 URL 필드로 전송
      aspect_ratio: options.aspect_ratio || '16:9',
      num_images: options.num_images || 1,
      seed: options.seed,
      guidance_scale: options.guidance_scale || 5.0,
      sync_mode: options.sync_mode || false,
      output_format: options.output_format || 'jpeg',
      safety_tolerance: options.safety_tolerance || '2',
    };

    const response = await API.post('/api/ai/image-to-image/flux-kontext-multi', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Flux Kontext Multi 변환에 실패했습니다.');
    }
  }

  /**
   * GPT Image 1 Edit - OpenAI DALL-E 이미지 편집
   * @param imageFiles 원본 이미지 파일들
   * @param prompt 편집 설명
   * @param options 추가 옵션들
   * @returns 편집된 이미지 정보
   */
  async generateWithGPTImageEdit(
    imageFiles: File[],
    prompt: string,
    options: {
      image_size?: string; // '1024x1024' | '1536x1024' | '1024x1536';
      num_images?: number; // 생성할 이미지 수
      quality?: 'standard' | 'high'; // 품질
      background?: 'transparent' | 'opaque' | 'auto';
      signal?: AbortSignal;
    } = {},
  ) {
    if (imageFiles.length === 0) {
      throw new Error('최소 1개의 이미지가 필요합니다.');
    }

    // API 키는 서버에서 처리됩니다
    // 모든 이미지를 Base64로 변환
    const imageBase64Array = await Promise.all(
      imageFiles.map((file) => this.convertFileToBase64(file)),
    );

    const requestData = {
      image_urls: imageBase64Array,
      prompt,
      image_size: options.image_size || '1024x1024',
      num_images: options.num_images || 1,
      quality: options.quality || 'high',
      background: options.background || 'auto',
    };

    const response = await API.post('/api/ai/image-to-image/gpt-image-1-edit', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'GPT Image Edit 변환에 실패했습니다.');
    }
  }

  /**
   * File을 Base64로 변환하는 헬퍼 메서드
   * @param file 변환할 파일
   * @returns Base64 문자열
   */
  private async convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = () => reject(new Error('파일을 Base64로 변환하는데 실패했습니다.'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Nano Banana를 사용하여 이미지-투-이미지 변환
   * @param imageFile 원본 이미지 파일
   * @param prompt 변환 설명
   * @param options 추가 옵션들
   * @returns 변환된 이미지 정보
   */
  async generateWithNanoBanana(
    imageFiles: File[],
    prompt: string,
    options: {
      num_images?: number; // 생성할 이미지 수
      sync_mode?: boolean; // 동기 모드
      output_format?: 'jpeg' | 'png'; // 출력 형식
      signal?: AbortSignal;
      aspect_ratio?: string;
    } = {},
  ) {
    // 이미지를 Base64로 변환
    const imageBase64Array = await Promise.all(
      imageFiles.map((imageFile) => this.convertFileToBase64(imageFile)),
    );
    const requestData = {
      prompt,
      image_urls: imageBase64Array, // Backend expects array of image URLs/Base64
      num_images: options.num_images || 1,
      output_format: options.output_format || 'jpeg',
      sync_mode: options.sync_mode || false,
      aspect_ratio: options.aspect_ratio || '3:4',
    };

    const response = await API.post('/api/ai/image-to-image/nano-banana-edit', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Nano Banana 변환에 실패했습니다.');
    }
  }

  /**
   * 사용 가능한 모델 목록
   */
  getAvailableModels() {
    return [
      { value: 'flux-pro-redux', label: 'Flux Pro Redux (고품질 변환)' },
      { value: 'flux-kontext', label: 'Flux Kontext (고급 편집)' },
      { value: 'flux-kontext-multi', label: 'Flux Kontext Multi (다중 이미지)' },
      { value: 'gpt-image-edit', label: 'GPT Image Edit (DALL-E)' },
      { value: 'nano-banana', label: 'Nano Banana (빠른 변환)' },
    ];
  }

  /**
   * 이미지 프롬프트 강도별 설명
   */
  getImagePromptStrengthDescriptions() {
    return {
      low: '0.1-0.3: 원본 이미지를 거의 그대로 유지하면서 미세한 변화만 적용',
      medium: '0.4-0.7: 원본과 새로운 스타일 사이의 균형잡힌 변환',
      high: '0.8-1.0: 원본 이미지를 크게 변화시켜 새로운 스타일로 완전 변환',
    };
  }

  /**
   * 기능별 설명
   */
  getFeatureDescriptions() {
    return {
      'flux-pro-redux': '고품질 이미지 변환 - 원본 이미지의 구조를 유지하면서 스타일을 변경',
      'flux-kontext': '고급 이미지 편집 - CFG 가이던스로 세밀한 제어 가능',
      'flux-kontext-multi': '다중 이미지 처리 - 최대 4개의 이미지를 일관된 스타일로 변환',
      'gpt-image-edit': 'DALL-E 기반 편집 - OpenAI의 고품질 이미지 편집 (API 키 필요)',
      'nano-banana': '빠른 이미지 변환 - 가볍고 빠른 처리속도로 간단한 이미지 변환',
    };
  }
}
